import { Crumb } from '../components/Shared/Breadcrumb/Breadcrumb';

// Adjust the crumbs to remove unnecessary ones and add specific ones.
// Call this function before the crumb text transformation.
export const adjustCrumbs = (crumbs: Crumb[]): Crumb[] => {
  if (!Array.isArray(crumbs) || crumbs.length === 0) {
    return [];
  }

  // Remove specific crumbs that are not needed in the breadcrumb trail.
  crumbs = crumbs.filter(crumb => {
    const text = crumb.text.toLowerCase();
    return text !== 'categories' && text !== 'calculators' && text !== 'team';
  });

  // Special case for community-lending since it is not in 'manufactured-home-loans'.
  // Add a breadcrumb for manufactured-home-loans before community-lending (but don't change community lending crumb).
  const index = crumbs.findIndex(
    crumb => crumb.text.toLowerCase() === 'community-lending'
  );
  if (index >= 0) {
    crumbs.splice(index, 0, {
      text: 'manufactured-home-loans',
      url: '/manufactured-home-loans',
    });
  }

  return crumbs;
};

// Transform the text for specific breadcrumbs.
// This is done to match text in the navigation menu dropdown.
export const transformCrumbText = (text: string): string => {
  const map: Record<string, string> = {
    'land-home-mortgage': 'Land & Home Loan',
    'documents-forms': 'Documents & Forms',
    faqs: 'FAQs',
    calculators: 'Mortgage Calculators',
    'extra-monthly-principal': 'Extra Monthly Principal Calculator',
    'biweekly-extra-principal': 'Biweekly Extra Principal Calculator',
    resources: 'Mortgage Resources',
  };
  return map[text] || formatCrumbText(text);
};

// Function to generate a text string from a breadcrumb.
export const formatCrumbText = (text: string): string => {
  // Replace consecutive hyphens or underscores with a single space
  const spacedText = text.toLowerCase().replace(/[-_]+/g, ' ');
  // Capitalize first letter of each word
  return text
    ? spacedText
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : text;
};

// Function to generate breadcrumbs from a given pathname
export const getBreadcrumbs = (pathname: string): Crumb[] => {
  // Split the pathname into segments, filter out empty strings, and map to breadcrumb objects
  let breadcrumbs: Crumb[] = [];

  if (pathname) {
    // 1. Generate raw crumbs with original text
    breadcrumbs = pathname
      .split('/')
      .filter(Boolean)
      .map((crumb, index, array) => ({
        text: crumb, // keep raw text here
        url: `/${array.slice(0, index + 1).join('/')}`,
      }));

    // 2. Adjust crumbs before transforming text
    breadcrumbs = adjustCrumbs(breadcrumbs);

    // 3. Transform text after adjustment
    breadcrumbs = breadcrumbs.map(crumb => ({
      ...crumb,
      text: transformCrumbText(crumb.text),
    }));
  }
  // Add home as first breadcrumb if there are other breadcrumbs.
  if (breadcrumbs.length > 0) {
    breadcrumbs.unshift({ text: 'Home', url: '/' });
  }

  return breadcrumbs;
};
